import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';

const DateRangePicker = ({ 
  value, 
  onChange, 
  label = "Date Range",
  className = "" 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Preset date ranges
  const presetRanges = [
    { value: 'all', label: 'All Time', description: 'Show all invoices' },
    { value: 'today', label: 'Today', description: new Date().toLocaleDateString() },
    { value: 'yesterday', label: 'Yesterday', description: new Date(Date.now() - 86400000).toLocaleDateString() },
    { value: 'last7days', label: 'Last 7 Days', description: 'Past week' },
    { value: 'last30days', label: 'Last 30 Days', description: 'Past month' },
    { value: 'last90days', label: 'Last 90 Days', description: 'Past quarter' },
    { value: 'thisMonth', label: 'This Month', description: getMonthName(new Date()) },
    { value: 'lastMonth', label: 'Last Month', description: getMonthName(new Date(new Date().setMonth(new Date().getMonth() - 1))) },
    { value: 'thisYear', label: 'This Year', description: new Date().getFullYear().toString() },
    { value: 'lastYear', label: 'Last Year', description: (new Date().getFullYear() - 1).toString() }
  ];

  function getMonthName(date) {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  const handlePresetSelect = (preset) => {
    onChange({ type: 'preset', value: preset });
    setIsOpen(false);
  };

  const handleCustomRangeApply = () => {
    if (customStartDate && customEndDate) {
      onChange({ 
        type: 'custom', 
        startDate: customStartDate, 
        endDate: customEndDate 
      });
      setIsOpen(false);
    }
  };

  const handleCustomRangeClear = () => {
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const getDisplayText = () => {
    if (value.type === 'custom' && value.startDate && value.endDate) {
      const start = new Date(value.startDate).toLocaleDateString();
      const end = new Date(value.endDate).toLocaleDateString();
      return `${start} - ${end}`;
    }
    
    const preset = presetRanges.find(p => p.value === value.value);
    return preset ? preset.label : 'All Time';
  };

  const isCustomRange = value.type === 'custom' && value.startDate && value.endDate;

  return (
    <div className={`date-range-picker ${className}`} ref={dropdownRef}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <div className="relative">
        <button
          type="button"
          className={`relative w-full bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm ${isOpen ? 'ring-1 ring-blue-500 border-blue-500' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="flex items-center">
            <Calendar size={14} className="mr-2 text-gray-400" />
            <span className={value.value === 'all' && !isCustomRange ? 'text-gray-500' : 'text-gray-900'}>
              {getDisplayText()}
            </span>
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown 
              size={16} 
              className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </span>
        </button>

        {isOpen && (
          <div 
            className="absolute z-50 mt-1 bg-white shadow-lg rounded-md border border-gray-200 overflow-hidden"
            style={{ 
              width: '420px',
              maxHeight: '450px',
              overflowY: 'auto'
            }}
          >
            {/* Preset Ranges */}
            <div className="p-4 border-b border-gray-200">
              <h6 className="text-sm font-semibold text-blue-600 mb-3">Quick Select</h6>
              <div className="grid grid-cols-2 gap-2">
                {presetRanges.map(preset => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`p-2 text-left rounded-md border text-sm transition-colors ${
                      value.value === preset.value && value.type !== 'custom' 
                        ? 'bg-blue-50 border-blue-300 text-blue-800' 
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => handlePresetSelect(preset.value)}
                    style={{ 
                      minHeight: '50px',
                      lineHeight: '1.2'
                    }}
                  >
                    <div className="font-medium">{preset.label}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {preset.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Range */}
            <div className="p-4">
              <h6 className="text-sm font-semibold text-blue-600 mb-3">Custom Range</h6>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  onClick={handleCustomRangeApply}
                  disabled={!customStartDate || !customEndDate}
                >
                  Apply Range
                </button>
                <button
                  type="button"
                  className="px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  onClick={handleCustomRangeClear}
                  disabled={!customStartDate && !customEndDate}
                >
                  <X size={14} />
                </button>
              </div>
              {customStartDate && customEndDate && (
                <div className="mt-2 text-sm text-green-600 flex items-center">
                  <Calendar size={12} className="mr-1" />
                  {new Date(customStartDate).toLocaleDateString()} - {new Date(customEndDate).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;